import React from 'react';
import { Link } from 'react-router-dom';
import { OpsTask, CRMUser } from '../../types';
import { RefreshCw, Flag, ListChecks } from 'lucide-react';
import { getActionRequiredShortLabel } from '../../utils/actionRequiredDisplay';
import { opsOriginHref, opsOriginLabel } from '../../utils/opsOriginDisplay';

const PRIORITY_COLORS: Record<string, string> = {
  low: '#94A3B8',
  medium: '#3B82F6',
  high: '#F59E0B',
  urgent: '#EF4444',
};

const CATEGORY_LABELS: Record<string, string> = {
  admin: 'Admin',
  maintenance: 'Maintenance',
  ordering: 'Ordering',
  requests: 'Requests',
  other: 'Other',
};

interface OpsTaskCardProps {
  task: OpsTask;
  teamMembers: CRMUser[];
  onClick: () => void;
  animDelay?: number;
}

const OpsTaskCard: React.FC<OpsTaskCardProps> = ({ task, teamMembers, onClick, animDelay = 0 }) => {
  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'completed';
  const assignee = task.assignedTo?.[0] ? teamMembers.find(u => u.uid === task.assignedTo[0]) : null;
  const originLabel = opsOriginLabel(task.origin);
  const originHref = opsOriginHref(task);

  const getInitials = (name: string) => {
    const p = name.split(' ');
    return p.length >= 2 ? `${p[0][0]}${p[1][0]}` : name.substring(0, 2);
  };

  const fmtDate = (d: Date) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div
      className={`ops-card ${isOverdue ? 'overdue' : ''}`}
      onClick={onClick}
      style={{ animationDelay: `${animDelay}ms` }}
    >
      <div className="ops-card-top">
        <span className="ops-card-priority-dot" style={{ background: PRIORITY_COLORS[task.priority] }} />
        <span className="ops-card-category">{CATEGORY_LABELS[task.category] || task.category}</span>
        {task.recurring && <RefreshCw size={10} className="ops-card-recurring" />}
        {task.actionRequired && (
          <span className="ops-card-action-flag">
            <Flag size={9} /> {getActionRequiredShortLabel(task.actionRequired, task.actionRequiredNote, teamMembers)}
          </span>
        )}
      </div>
      <div className="ops-card-title">{task.title}</div>
      {originLabel && (
        originHref ? (
          <Link
            to={originHref}
            className={`ops-origin-badge ops-origin-${task.origin?.kind || 'manual'}`}
            onClick={(e) => e.stopPropagation()}
            title={originLabel}
          >
            {originLabel}
          </Link>
        ) : (
          <span className={`ops-origin-badge ops-origin-${task.origin?.kind || 'manual'}`} title={originLabel}>
            {originLabel}
          </span>
        )
      )}
      <div className="ops-card-bottom">
        {task.dueDate && (
          <span className={`ops-card-due ${isOverdue ? 'overdue' : ''}`}>
            {fmtDate(task.dueDate)}
          </span>
        )}
        {task.subtasks && task.subtasks.length > 0 && (
          <span
            className="ops-card-subtasks"
            title={`Checklist ${task.subtasks.filter(s => s.completed).length}/${task.subtasks.length}`}
          >
            <ListChecks size={10} aria-hidden />
            {task.subtasks.filter(s => s.completed).length}/{task.subtasks.length}
          </span>
        )}
        <span className="ops-card-spacer" />
        {assignee && (
          <div className="ops-card-avatar" title={assignee.displayName}>
            {getInitials(assignee.displayName)}
          </div>
        )}
      </div>
    </div>
  );
};

export default OpsTaskCard;
